import { HTMLProps, MouseEvent, forwardRef } from 'react';
import { LinkProps } from 'react-router-dom';

import classNames from 'classnames';

import { TableRow } from '@spotify-internal/encore-web';

import { useNavigate } from '../../behaviors';

import styles from './LinkableTableRow.module.css';

const isExternalUrl = (to: LinkProps['to']): boolean =>
  typeof to === 'string' && /^([a-z+.-]+):/.test(to);

export type LinkableTableRowProps = Omit<
  HTMLProps<HTMLTableRowElement>,
  'ref' | 'as'
> & {
  to: LinkProps['to'];
  hover?: boolean;
};

export const LinkableTableRow = forwardRef<
  HTMLTableRowElement,
  LinkableTableRowProps
>(({ children, to, hover = true, className, ...tableRowProps }, ref) => {
  const navigate = useNavigate();

  const handleClick = (event: MouseEvent<HTMLTableRowElement>) => {
    if (!to) return;

    const target = event.target as Element | null;
    if (target?.closest('a')) return;

    const url = typeof to === 'string' ? to : to.pathname || '';
    const external = isExternalUrl(to);

    if (event.metaKey || event.ctrlKey || external) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }

    navigate(to);
  };

  return (
    <TableRow
      ref={ref}
      hover={hover}
      className={classNames(styles.row, className)}
      onClick={handleClick}
      data-track-source-anchor="f6f2c0b5cf;linkable-table-row-table-row" {...tableRowProps}
    >
      {children}
    </TableRow>
  );
});
LinkableTableRow.displayName = 'LinkableTableRow';
